import React from 'react';

import {
  ListItem,
  ListItemButton,
  ListItemProps,
  ListItemText,
  listItemButtonClasses,
  TooltipProps,
} from '@mui/material';
import { matchPath, useLocation } from 'react-router';

import { appRoutes } from '@clearverify/constants';
import { Nullable } from '@clearverify/types';

import { Link } from 'src/components/common/Link';
import { Tooltip } from 'src/components/common/Tooltip';
import { SxProp } from 'src/types/mui';

import { NavListItemIcon } from './components';

interface CompoundComponent {
  Icon: React.FC<React.ComponentProps<typeof NavListItemIcon>>;
  Text: React.FC<React.ComponentProps<typeof ListItemText>>;
}

type NavListItemProps = {
  sx?: SxProp;
  key?: string;
  link?: string;
  onClick?: () => void;
  backgroundColor?: string;
  textColor?: string;
  iconColor?: string;
  selectedBackgroundColor?: string;
  selectedColor?: string;
  selectedIconColor?: string;
  color?: string;
  alignItems?: ListItemProps['alignItems'];
  dense?: boolean;
  disabled?: boolean;
  disableGutters?: boolean;
  divider?: boolean;
  tooltipTitle?: string;
  tooltipPlacement?: TooltipProps['placement'];
  startIcon?: React.ReactNode;
  endIcon?: React.ReactNode;
  listItemText?: React.ReactNode;
  selected?: boolean;
  autoFocus?: boolean;
};

export const NavListItem: React.FC<React.PropsWithChildren<NavListItemProps>> & CompoundComponent =
  props => {
    const location = useLocation();

    const selected = Boolean(
      props.link && matchPath({ path: `${props.link}/*` }, location.pathname),
    );

    const handleClick = React.useCallback(() => {
      if (props.disabled) return;
      props.onClick?.();
    }, [props.disabled, props.onClick]);

    return (
      <Tooltip placement={props.tooltipPlacement} title={props.tooltipTitle}>
        <ListItem
          sx={props.sx}
          onClick={handleClick}
          alignItems={props.alignItems}
          dense={props.dense}
          disableGutters={props.disableGutters}
          divider={props.divider}
        >
          <ListItemButton
            data-e2e-element="navigation-button"
            component={Link}
            to={props.link ?? appRoutes.root}
            sx={makeSelectedItemStyles({
              selectedItemBgColor: props.selectedBackgroundColor,
              selectedItemColor: props.selectedColor,
            })}
            selected={props.selected !== undefined ? props.selected : selected}
            disabled={props.disabled}
          >
            {props.startIcon && props.startIcon}
            {props.listItemText}
            {props.endIcon && props.endIcon}
          </ListItemButton>
        </ListItem>
      </Tooltip>
    );
  };

const makeSelectedItemStyles =
  ({
    selectedItemBgColor,
    selectedItemColor,
  }: {
    selectedItemBgColor: Nullable<string>;
    selectedItemColor: Nullable<string>;
  }): SxProp =>
  theme => ({
    borderRadius: '4px',
    [`&.${listItemButtonClasses.selected}`]: {
      backgroundColor: `${selectedItemBgColor || theme.palette.action.focus} !important`,
      color: selectedItemColor || theme.palette.text.primary,
      '&:hover': {
        backgroundColor: `${selectedItemBgColor || theme.palette.action.focus} !important`,
      },
    },
    justifyContent: 'center',
  });

NavListItem.Icon = NavListItemIcon;
NavListItem.Text = ListItemText;
